import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, LifeBuoy, ArrowRight, Send } from "lucide-react";
import api from "@/lib/api";

const CONTEXT = {
  "/": "What would you like to create today? Describe an idea and I'll plan the film.",
  "/create": "Tell me your idea in one line — I'll turn it into a production plan.",
  "/projects": "This is your film library. Open a film to continue its production.",
  "/production": "This is your control room. Your next production step is highlighted on the pipeline.",
  "/cinematic-planner": "Need help organizing a scene, shot or character? I can guide the plan.",
  "/video-engine": "Pick a scene and shot — the preview stage is ready when you are.",
  "/voice-studio": "Choose a character and take to design the narration.",
  "/sound-library": "Layer music, SFX and ambience to build the film's atmosphere.",
  "/brand-studio": "Set your film's visual identity here — it carries across every project.",
  "/assets": "Every generated frame lives in this vault. Search or filter to find one.",
  "/export-center": "Your film is nearly ready — choose a format to render the final cut.",
  "/publishing": "Ready to share your story? Connect a destination to publish.",
  "/billing": "Your Vyom credits power AI production. Ask me how credits work.",
  "/settings": "Need help with your account, plan or preferences? I'm here.",
};

export default function Nova() {
  const [open, setOpen] = useState(false);
  const [acct, setAcct] = useState(null);
  const [chat, setChat] = useState([]); // {role, text}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const msg = CONTEXT[pathname] || "I'm Nova, your AI production assistant. How can I help?";

  useEffect(() => {
    if (open && !acct) api.get("/nova/account-summary").then(({ data }) => setAcct(data)).catch(() => {});
  }, [open, acct]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat, busy]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setChat((c) => [...c, { role: "user", text }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/nova/ask", { question: text });
      setChat((c) => [...c, { role: "nova", text: data.answer }]);
      if (data.facts) setAcct((a) => ({ ...(a || {}), balance: data.facts.balance, watermark_free: data.facts.watermark_free, generations: { total: data.facts.generations, failed: data.facts.failed } }));
    } catch {
      setChat((c) => [...c, { role: "nova", text: "I couldn't reach your account records just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const SUGGESTIONS = ["How many credits do I have?", "Why did my last generation fail?", "What did I purchase?", "Why can't I export without a watermark?"];

  return (
    <div className="fixed bottom-5 right-5 z-40" data-testid="nova">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            className="absolute bottom-16 right-0 w-[340px] max-w-[88vw] rounded-2xl border border-white/10 bg-[#12121f]/95 backdrop-blur-xl p-4 shadow-2xl flex flex-col"
            data-testid="nova-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#5B4DFF] to-[#7C70FF] text-white"><Sparkles className="w-4 h-4" /></span>
              <span className="font-display font-semibold text-white">Nova</span>
              <span className="text-[11px] text-slate-500">AI account assistant</span>
              <button onClick={() => setOpen(false)} className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid="nova-close"><X className="w-4 h-4" /></button>
            </div>

            {chat.length === 0 && <p className="text-sm text-slate-300 leading-relaxed">{msg}</p>}

            {acct && chat.length === 0 && (
              <div className="mt-3 rounded-xl bg-white/[0.04] border border-white/10 p-3 text-xs" data-testid="nova-account">
                <div className="flex items-center justify-between"><span className="text-slate-500">Credits</span><span className="text-[#FFB300] font-semibold">{acct.balance?.toLocaleString()}</span></div>
                <div className="flex items-center justify-between mt-1"><span className="text-slate-500">Generations</span><span className="text-slate-300">{acct.generations?.total} ({acct.generations?.failed} failed)</span></div>
                <div className="flex items-center justify-between mt-1"><span className="text-slate-500">Watermark</span><span className="text-slate-300">{acct.watermark_free ? "removed" : "included"}</span></div>
              </div>
            )}

            {chat.length > 0 && (
              <div ref={scrollRef} className="mt-2 max-h-[260px] overflow-y-auto space-y-2 pr-1" data-testid="nova-chat">
                {chat.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                    <span className={"inline-block px-3 py-2 rounded-2xl text-sm max-w-[85%] " + (m.role === "user" ? "bg-[#5B4DFF] text-white" : "bg-white/[0.06] text-slate-200 border border-white/10")}>{m.text}</span>
                  </div>
                ))}
                {busy && <div className="text-left"><span className="inline-block px-3 py-2 rounded-2xl text-sm bg-white/[0.06] text-slate-400 border border-white/10">Nova is checking your records…</span></div>}
              </div>
            )}

            {chat.length === 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="nova-suggestion"
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-[#5B4DFF]/50 hover:text-white transition">{s}</button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex items-center gap-2">
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nova-input"
                placeholder="Ask about your credits, plan, generations…"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#5B4DFF]/50" />
              <button type="submit" disabled={busy} data-testid="nova-send"
                className="p-2 rounded-xl bg-[#5B4DFF] text-white hover:bg-[#4B3DE6] transition disabled:opacity-50"><Send className="w-4 h-4" /></button>
            </form>

            <button onClick={() => { navigate("/help"); setOpen(false); }} data-testid="nova-help"
              className="mt-2.5 w-full inline-flex items-center justify-center gap-2 py-2 rounded-xl bg-white/8 border border-white/12 text-white text-sm hover:border-[#5B4DFF]/50 transition">
              <LifeBuoy className="w-4 h-4" /> Open Help Center <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="nova-orb"
        className="w-14 h-14 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#7C70FF] grid place-items-center text-white shadow-[0_0_30px_rgba(91,77,255,0.6)] ring-2 ring-white/15 hover:scale-105 transition"
        title="Nova — your AI production assistant"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    </div>
  );
}
